import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import Swal from 'sweetalert2';

interface LeaveForm {
  employee_id: string | null;
  employee_name: string | null;
  from_date: string | null;
  to_date: string | null;
  leave_type: string;
  notes: string | null;
}

@Component({
  selector: 'app-attendance',
  templateUrl: './attendance.component.html',
  styleUrls: ['./attendance.component.css'],
  standalone: false
})
export class AttendanceComponent implements OnInit {

  page = 1;
  pageSize = 10;

  allItems: any[] = [];
  displayItems: any[] = [];
  employees: any[] = [];

  list_length = 0;
  filter_date: string | null = null;
  searchTerm = '';

  token: string | null;
  modalMode: 'create' | 'edit' = 'create';
  editingId: string | null = null;
  submitting = false;

  form: LeaveForm = this.emptyForm();

  readonly leaveTypes = [
    { value: 'ANNUAL', label: 'Annual', badge: 'bg-primary' },
    { value: 'SICK', label: 'Sick', badge: 'bg-warning text-dark' },
    { value: 'PERSONAL', label: 'Without Payment', badge: 'bg-secondary' },
    { value: 'LEAVE', label: 'Leave', badge: 'bg-info text-dark' },
    { value: 'LONG', label: 'Long', badge: 'bg-dark' },
    { value: 'OTHER', label: 'Other', badge: 'bg-light text-dark border' }
  ];

  constructor(
    public datepipe: DatePipe,
    public utility: UtilitiesService,
    private modalService: NgbModal,
    private api: ApiService
  ) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
  }

  ngOnInit(): void {
    this.loadData();
  }

  get paginatedItems(): any[] {
    const start = (this.page - 1) * this.pageSize;
    return this.displayItems.slice(start, start + this.pageSize);
  }

  get isFormValid(): boolean {
    return !!(
      this.form.employee_id &&
      this.form.from_date &&
      this.form.to_date &&
      this.form.leave_type &&
      this.form.leave_type !== '0' &&
      this.previewDays !== null &&
      this.previewDays > 0
    );
  }

  get previewDays(): number | null {
    if (!this.form.from_date || !this.form.to_date) {
      return null;
    }
    return this.calcDays(this.form.from_date, this.form.to_date);
  }

  openCreate(modal: unknown): void {
    this.modalMode = 'create';
    this.editingId = null;
    this.form = this.emptyForm();
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  openEdit(modal: unknown, leave: any): void {
    this.modalMode = 'edit';
    this.editingId = leave.id;
    this.form = {
      employee_id: leave.employee_id,
      employee_name: leave.employee_name,
      from_date: this.toInputDate(leave.from_date),
      to_date: this.toInputDate(leave.to_date),
      leave_type: leave.leave_type ?? 'ANNUAL',
      notes: leave.notes
    };
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  loadData(): void {
    this.utility.loading = true;
    forkJoin({
      leaves: this.api.get('leaves/', this.token),
      employees: this.api.get('employees/', this.token)
    }).subscribe({
      next: ({ leaves, employees }) => {
        let leaveData: any = leaves ?? [];
        let empData: any = employees ?? [];
        this.allItems = this.enrichLeaves(leaveData);
        this.employees = empData.map((row: any) => row.employee ?? row);
        this.applyFilters();
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'Could not load leave requests.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.utility.loading = false;
      }
    });
  }

  applyFilters(): void {
    let result = [...this.allItems];

    if (this.filter_date) {
      const month = this.datepipe.transform(this.filter_date, 'yyyy-MM');
      result = result.filter(
        i => this.datepipe.transform(i.from_date, 'yyyy-MM') === month
      );
    }

    const query = this.searchTerm.trim().toLowerCase();
    if (query) {
      result = result.filter(i =>
        (i.employee_name ?? '').toLowerCase().includes(query)
      );
    }

    this.displayItems = result;
    this.list_length = result.length;
    this.page = 1;
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  filterDate(): void {
    this.applyFilters();
  }

  clearFilter(): void {
    this.filter_date = null;
    this.searchTerm = '';
    this.applyFilters();
  }

  getLeaveTypeMeta(type: string) {
    return this.leaveTypes.find(t => t.value === type) ?? {
      value: type,
      label: type,
      badge: 'bg-secondary'
    };
  }

  saveLeave(): void {
    if (!this.isFormValid || this.submitting) {
      return;
    }

    const employee = this.employees.find(i => i.id === this.form.employee_id);
    const body = {
      employee_id: this.form.employee_id,
      employee_name: employee?.name ?? '',
      from_date: this.form.from_date,
      to_date: this.form.to_date,
      leave_type: this.form.leave_type,
      notes: this.form.notes
    };

    this.submitting = true;
    const request =
      this.modalMode === 'edit' && this.editingId
        ? this.api.update('leaves/' + this.editingId, body, this.token)
        : this.api.post('leaves/', body, this.token);

    request.subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.loadData();
        Swal.fire({
          title: 'Success',
          text:
            this.modalMode === 'edit'
              ? 'Leave request updated.'
              : 'Leave request created.',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000,
          showConfirmButton: true
        });
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'Something went wrong. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  onDelete(id: string): void {
    Swal.fire({
      title: 'Delete leave request?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }

      this.api.delete('leaves/' + id, this.token).subscribe({
        next: () => {
          this.loadData();
          Swal.fire({
            title: 'Deleted',
            text: 'Leave request removed.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        },
        error: () => {
          Swal.fire({
            title: 'Error',
            text: 'Could not delete this request.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      });
    });
  }

  private emptyForm(): LeaveForm {
    return {
      employee_id: null,
      employee_name: null,
      from_date: null,
      to_date: null,
      leave_type: 'ANNUAL',
      notes: null
    };
  }

  private enrichLeaves(data: any[]): any[] {
    return data.map(item => ({
      ...item,
      dayDiff: this.calcDays(item.from_date, item.to_date)
    }));
  }

  private calcDays(from: string, to: string): number {
    const date1 = new Date(from);
    const date2 = new Date(to);
    const timeDiff = date2.getTime() - date1.getTime();
    return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
  }

  private toInputDate(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return value;
    }
    return d.toISOString().slice(0, 10);
  }
}
